import { useState } from 'react'
import { Button } from '../catalyst/button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../catalyst/dialog'
import { ErrorMessage, Field, FieldGroup, Label } from '../catalyst/fieldset'
import { Input } from '../catalyst/input'
import { Select } from '../catalyst/select'
import { Text } from '../catalyst/text'
import { useFarms, type FarmInput } from '../lib/farms'
import { formErrors, noErrors, type FormErrors } from '../lib/form-errors'
import type { Farm } from '../lib/types'

const CA_PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT']

/** Adds a farm, or edits `farm`: its name, address and location pin. */
export function FarmFormDialog({ open, onClose, farm }: { open: boolean; onClose: () => void; farm?: Farm }) {
  const { addFarm, updateFarm } = useFarms()
  const [name, setName] = useState(farm?.name ?? '')
  const [addressLine1, setAddressLine1] = useState(farm?.addressLine1 ?? '')
  const [addressLine2, setAddressLine2] = useState(farm?.addressLine2 ?? '')
  const [city, setCity] = useState(farm?.city ?? '')
  const [province, setProvince] = useState(farm?.province ?? 'BC')
  const [postalCode, setPostalCode] = useState(farm?.postalCode ?? '')
  const [country, setCountry] = useState(farm?.country ?? 'CA')
  const [latitude, setLatitude] = useState(farm?.latitude == null ? '' : String(farm.latitude))
  const [longitude, setLongitude] = useState(farm?.longitude == null ? '' : String(farm.longitude))
  const [locating, setLocating] = useState(false)
  const [locationNote, setLocationNote] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<FormErrors>(noErrors)

  const invalid = (key: string) => key in errors.fields
  const message = (key: string) => (invalid(key) ? <ErrorMessage>{errors.fields[key]}</ErrorMessage> : null)

  function useMyLocation() {
    setLocationNote(null)
    if (!navigator.geolocation) {
      setLocationNote('This browser cannot share its location. Type the coordinates instead.')
      return
    }
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude.toFixed(6))
        setLongitude(position.coords.longitude.toFixed(6))
        setLocating(false)
      },
      () => {
        setLocationNote('Could not get your location. Allow location access, or type the coordinates instead.')
        setLocating(false)
      },
    )
  }

  /** A coordinate box: empty is "not set"; anything else must be a number (the server checks the range). */
  const coordinate = (value: string) => (value.trim() === '' ? null : Number(value))

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!name.trim()) return
    const lat = coordinate(latitude)
    const lng = coordinate(longitude)
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      setErrors({ fields: { [Number.isNaN(lat) ? 'latitude' : 'longitude']: 'Enter a number, like 49.1044' }, general: null })
      return
    }
    const input: FarmInput = {
      name: name.trim(),
      addressLine1: addressLine1.trim() || null,
      addressLine2: addressLine2.trim() || null,
      city: city.trim() || null,
      province: province.trim(),
      postalCode: postalCode.trim() || null,
      country: country.trim(),
      latitude: lat,
      longitude: lng,
    }
    setSubmitting(true)
    setErrors(noErrors)
    try {
      if (farm) {
        await updateFarm(farm.id, input)
      } else {
        // A new farm only needs what was filled in; the rest keeps the server's defaults.
        await addFarm(Object.fromEntries(Object.entries(input).filter(([, v]) => v !== null && v !== '')) as FarmInput & { name: string })
      }
      onClose()
    } catch (err) {
      setErrors(formErrors(err, farm ? 'Could not save farm.' : 'Could not add farm.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{farm ? 'Edit farm' : 'Add farm'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogBody>
          {errors.general && <Text className="text-red-600 dark:text-red-400">{errors.general}</Text>}
          <FieldGroup>
            <Field>
              <Label>Farm name</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} invalid={invalid('name')} autoFocus required />
              {message('name')}
            </Field>
            <Field>
              <Label>Address</Label>
              <Input value={addressLine1} onChange={(event) => setAddressLine1(event.target.value)} invalid={invalid('addressLine1')} autoComplete="address-line1" />
              {message('addressLine1')}
            </Field>
            <Field>
              <Label>Address line 2</Label>
              <Input value={addressLine2} onChange={(event) => setAddressLine2(event.target.value)} invalid={invalid('addressLine2')} autoComplete="address-line2" />
              {message('addressLine2')}
            </Field>
            <Field>
              <Label>City</Label>
              <Input value={city} onChange={(event) => setCity(event.target.value)} invalid={invalid('city')} autoComplete="address-level2" />
              {message('city')}
            </Field>
            <Field>
              <Label>Province</Label>
              {country.trim().toUpperCase() === 'CA' ? (
                <Select value={province} onChange={(event) => setProvince(event.target.value)} invalid={invalid('province')}>
                  {CA_PROVINCES.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input value={province} onChange={(event) => setProvince(event.target.value)} invalid={invalid('province')} />
              )}
              {message('province')}
            </Field>
            <Field>
              <Label>Postal code</Label>
              <Input value={postalCode} onChange={(event) => setPostalCode(event.target.value)} invalid={invalid('postalCode')} autoComplete="postal-code" />
              {message('postalCode')}
            </Field>
            <Field>
              <Label>Country</Label>
              <Input value={country} onChange={(event) => setCountry(event.target.value)} invalid={invalid('country')} maxLength={2} placeholder="CA" />
              {message('country')}
            </Field>
            <div className="space-y-4">
              <div>
                <div className="text-sm/6 font-medium text-zinc-950 dark:text-white">Location pin</div>
                <Text>Optional. Fill in both, or neither.</Text>
              </div>
              <Button type="button" outline onClick={useMyLocation} disabled={locating}>
                {locating ? 'Finding you…' : 'Use my current location'}
              </Button>
              {locationNote && <Text className="text-red-600 dark:text-red-400">{locationNote}</Text>}
              <div className="grid grid-cols-2 gap-4">
                <Field>
                  <Label>Latitude</Label>
                  <Input value={latitude} onChange={(event) => setLatitude(event.target.value)} invalid={invalid('latitude')} inputMode="decimal" placeholder="49.1044" />
                  {message('latitude')}
                </Field>
                <Field>
                  <Label>Longitude</Label>
                  <Input value={longitude} onChange={(event) => setLongitude(event.target.value)} invalid={invalid('longitude')} inputMode="decimal" placeholder="-122.6608" />
                  {message('longitude')}
                </Field>
              </div>
            </div>
          </FieldGroup>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : farm ? 'Save' : 'Add farm'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
